import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  IDoesFilterPassParams,
  IFilterParams,
  IRowNode,
} from 'ag-grid-community'
import {
  BulkSheetFilter,
  FilterFooter,
  FilterInput,
} from '../../../containers/BulkSheetView/components/filter/common'
import {
  SelectFilterArea,
  SelectFilterFormControl,
  CheckboxArea,
  FilterLabel,
} from '../../../containers/BulkSheetView/components/filter/SelectFilter'
import { Checkbox, Divider, Typography, styled } from '@mui/material'
import { intl } from '../../../../i18n'
import {
  ProductBacklogRow,
  SprintRow,
  isSprintRow,
  isProductBacklogItemRow,
} from '../productBacklog'
import {
  getWbsItemStatusColorCode,
  getWbsItemStatusDeepColorCode,
} from '../../../../lib/functions/wbsItem'
import { SprintStatus } from '../../../../lib/functions/sprint'
import { WbsItemStatus } from '../../../../domain/entity/WbsItemEntity'

export type StatusFilterValue = {
  status: string
  backgroundColor: string
  type: 'sprint' | 'wbsItem'
}

type Props = IFilterParams & {
  filterChangedCallback: () => void
  getLabel: (option: StatusFilterValue) => string
  sortValues: (options: StatusFilterValue[]) => StatusFilterValue[]
}

type FilterModel = {
  sprintValues: string[]
  pbiValues: string[]
  includeBlank: boolean
  includeNoSprint: boolean
}

const SectionLabel = styled(Typography)({
  fontSize: '11px',
  fontWeight: 'bold',
  color: '#666',
  marginTop: '8px',
  marginBottom: '4px',
})

const getSprintStatusColor = (status: SprintStatus): string => {
  switch (status) {
    case SprintStatus.INPROGRESS:
      return getWbsItemStatusColorCode(WbsItemStatus.DOING)
    case SprintStatus.FINISHED:
      return getWbsItemStatusColorCode(WbsItemStatus.DONE)
    case SprintStatus.STANDBY:
      return getWbsItemStatusDeepColorCode(WbsItemStatus.TODO)
    case SprintStatus.CANCELED:
      return getWbsItemStatusColorCode(WbsItemStatus.DISCARD)
  }
}

/**
 * ノードからステータスフィルター用の値を取得する
 */
const getStatusFilterValue = (
  node: IRowNode<ProductBacklogRow>
): StatusFilterValue | StatusFilterValue[] | undefined => {
  const data = node?.data
  if (!data) return undefined

  if (isSprintRow(data)) {
    const sprintStatus = data.sprintDetail?.status
    if (!sprintStatus) return undefined
    return {
      status: sprintStatus,
      backgroundColor: getSprintStatusColor(sprintStatus),
      type: 'sprint' as const,
    }
  }

  if (isProductBacklogItemRow(data)) {
    // PBI行は親スプリントのステータスとPBI自身のステータスを配列で返す
    // これにより、スプリントステータスでフィルターすると親子ともにフィルターされる
    const parentNode = node?.parent
    const parentSprintRow = parentNode?.data as SprintRow | undefined
    const parentSprintStatus = parentSprintRow?.sprintDetail?.status

    const wbsStatus = data.wbsItem.status
    const result: StatusFilterValue[] = []

    // 親スプリントのステータスを追加
    if (parentSprintStatus) {
      result.push({
        status: parentSprintStatus,
        backgroundColor: getSprintStatusColor(parentSprintStatus),
        type: 'sprint',
      })
    }

    // PBI自身のステータスを追加
    if (wbsStatus) {
      result.push({
        status: wbsStatus,
        backgroundColor: getWbsItemStatusDeepColorCode(wbsStatus),
        type: 'wbsItem',
      })
    }

    return result.length > 0 ? result : undefined
  }

  return undefined
}

/**
 * ProductBacklog専用のステータスフィルター
 * SprintステータスとPBIステータスを別セクションで表示し、独立して選択可能
 */
export const ProductBacklogStatusFilter = forwardRef(
  ({ filterChangedCallback, getLabel, sortValues, api }: Props, ref) => {
    // State
    const [text, setText] = useState('')
    const [options, setOptions] = useState<StatusFilterValue[]>([])
    const [sprintValues, setSprintValues] = useState<string[]>([])
    const [pbiValues, setPbiValues] = useState<string[]>([])
    const [includeBlank, setIncludeBlank] = useState(true)
    const [includeNoSprint, setIncludeNoSprint] = useState(true)
    const initialized = useRef(false)
    const restoredFilterState = useRef<boolean>()

    // オプションをタイプ別に分類
    const sprintOptions = useMemo(
      () => options.filter(o => o.type === 'sprint'),
      [options]
    )
    const pbiOptions = useMemo(
      () => options.filter(o => o.type === 'wbsItem'),
      [options]
    )

    // フィルターがアクティブかどうか
    const isFilterActive = useCallback(() => {
      const allSprintSelected =
        sprintValues.length === sprintOptions.length ||
        sprintOptions.length === 0
      const allPbiSelected =
        pbiValues.length === pbiOptions.length || pbiOptions.length === 0
      return !(
        allSprintSelected &&
        allPbiSelected &&
        includeBlank &&
        includeNoSprint
      )
    }, [
      sprintValues,
      pbiValues,
      includeBlank,
      includeNoSprint,
      sprintOptions.length,
      pbiOptions.length,
    ])

    // 全選択/全解除のヘルパー
    const isSprintAllSelected = useMemo(
      () =>
        sprintOptions.length > 0 &&
        sprintValues.length === sprintOptions.length,
      [sprintValues, sprintOptions]
    )
    const isPbiAllSelected = useMemo(
      () => pbiOptions.length > 0 && pbiValues.length === pbiOptions.length,
      [pbiValues, pbiOptions]
    )

    const selectAllSprint = useCallback(() => {
      setSprintValues(sprintOptions.map(o => o.status))
    }, [sprintOptions])

    const deselectAllSprint = useCallback(() => {
      setSprintValues([])
    }, [])

    const selectAllPbi = useCallback(() => {
      setPbiValues(pbiOptions.map(o => o.status))
    }, [pbiOptions])

    const deselectAllPbi = useCallback(() => {
      setPbiValues([])
    }, [])

    const reset = useCallback(() => {
      setSprintValues(sprintOptions.map(o => o.status))
      setPbiValues(pbiOptions.map(o => o.status))
      setIncludeBlank(true)
      setIncludeNoSprint(true)
    }, [sprintOptions, pbiOptions])

    const getOptions = useCallback((): StatusFilterValue[] => {
      const initialOptions: (StatusFilterValue | StatusFilterValue[])[] = []
      api.forEachNode(node => {
        const value = getStatusFilterValue(node)
        if (value) {
          initialOptions.push(value)
        }
      })

      // ユニークなオプションを抽出
      const newOptionValues: string[] = []
      const newOptions: StatusFilterValue[] = []

      initialOptions.forEach(option => {
        if (Array.isArray(option)) {
          option.forEach(item => {
            const key = `${item.type}-${item.status}`
            if (!newOptionValues.includes(key)) {
              newOptionValues.push(key)
              newOptions.push(item)
            }
          })
        } else {
          const key = `${option.type}-${option.status}`
          if (!newOptionValues.includes(key)) {
            newOptionValues.push(key)
            newOptions.push(option)
          }
        }
      })

      return sortValues ? sortValues(newOptions) : newOptions
    }, [api, sortValues])

    // Ag-grid custom filter
    useImperativeHandle(ref, () => {
      return {
        doesFilterPass(params: IDoesFilterPassParams) {
          // Don't filter new rows
          if (params.data?.isAdded) return true

          const selectValue = getStatusFilterValue(params.node)

          // スプリント未設定の親行の場合はincludeNoSprintで判定
          if (!selectValue && params.data?.rowType === 'sprint') {
            return includeNoSprint
          }

          if (includeBlank && !selectValue) return true
          if (!selectValue) return false

          if (Array.isArray(selectValue)) {
            // PBI行：親スプリントとPBI自身の両方をチェック
            const sprintStatus = selectValue.find(
              v => v.type === 'sprint'
            )?.status
            const pbiStatus = selectValue.find(
              v => v.type === 'wbsItem'
            )?.status

            // スプリント未設定のPBIの場合はincludeNoSprintで判定
            // スプリントステータスがある場合はそのステータスで判定
            const sprintPass = sprintStatus
              ? sprintValues.includes(sprintStatus)
              : includeNoSprint
            const pbiPass = !pbiStatus || pbiValues.includes(pbiStatus)

            return sprintPass && pbiPass
          }

          // スプリント行：スプリントステータスのみチェック
          if (selectValue.type === 'sprint') {
            return sprintValues.includes(selectValue.status)
          }
          // WbsItem行（スプリント未設定のPBI）
          // includeNoSprintがtrueの場合のみ表示
          return includeNoSprint && pbiValues.includes(selectValue.status)
        },

        isFilterActive() {
          return isFilterActive()
        },

        getModel(): FilterModel | undefined {
          if (isFilterActive()) {
            return { sprintValues, pbiValues, includeBlank, includeNoSprint }
          }
          return undefined
        },

        setModel(model: FilterModel | null) {
          if (!model) {
            reset()
            return
          }
          restoredFilterState.current = true
          const newOptions = getOptions()
          setOptions(newOptions)
          setSprintValues(model.sprintValues ?? [])
          setPbiValues(model.pbiValues ?? [])
          setIncludeBlank(model.includeBlank ?? true)
          setIncludeNoSprint(model.includeNoSprint ?? true)
        },

        afterGuiAttached() {
          setOptions(getOptions())
        },

        getModelAsString() {
          if (!isFilterActive()) return ''
          const labels: string[] = []
          if (includeNoSprint) {
            labels.push(
              intl.formatMessage({
                id: 'productBacklog.estimatesByAssignee.sprintUnassigned',
              })
            )
          }

          sprintOptions
            .filter(o => sprintValues.includes(o.status))
            .forEach(o => labels.push(getLabel(o)))

          pbiOptions
            .filter(o => pbiValues.includes(o.status))
            .forEach(o => labels.push(getLabel(o)))

          return labels.join(',')
        },
      }
    }, [
      sprintValues,
      pbiValues,
      includeBlank,
      includeNoSprint,
      getLabel,
      isFilterActive,
      sprintOptions,
      pbiOptions,
      reset,
      getOptions,
    ])

    useEffect(() => {
      if (restoredFilterState.current) {
        // 復元時は何もしない
        restoredFilterState.current = false
        initialized.current = true
      } else if (!initialized.current && options.length > 0) {
        // 初回のみ全選択
        setSprintValues(sprintOptions.map(o => o.status))
        setPbiValues(pbiOptions.map(o => o.status))
        initialized.current = true
      }
    }, [options])

    useEffect(() => {
      api.onFilterChanged()
      filterChangedCallback()
    }, [sprintValues, pbiValues, includeBlank, includeNoSprint])

    // 検索フィルター
    const filteredSprintOptions = sprintOptions.filter(option => {
      const label = getLabel(option)
      return (
        !text || (label && label.toLowerCase().includes(text.toLowerCase()))
      )
    })

    const filteredPbiOptions = pbiOptions.filter(option => {
      const label = getLabel(option)
      return (
        !text || (label && label.toLowerCase().includes(text.toLowerCase()))
      )
    })

    return (
      <BulkSheetFilter>
        <FilterInput
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={'Search...'}
          sx={{ height: 22 }}
        />
        <SelectFilterArea>
          {/* Sprint Status Section */}
          <SectionLabel>Sprint Status</SectionLabel>
          <SelectFilterFormControl
            label="(Select All)"
            control={
              <Checkbox
                checked={isSprintAllSelected && includeNoSprint}
                indeterminate={
                  !(isSprintAllSelected && includeNoSprint) &&
                  (sprintValues.length > 0 || includeNoSprint)
                }
                onChange={e => {
                  if (e.target.checked) {
                    selectAllSprint()
                    setIncludeNoSprint(true)
                  } else {
                    deselectAllSprint()
                    setIncludeNoSprint(false)
                  }
                }}
                disableRipple={true}
              />
            }
          />
          <CheckboxArea>
            {filteredSprintOptions.map(option => {
              const value = option.status
              return (
                <SelectFilterFormControl
                  key={`sprint-filter-${value}`}
                  label={
                    <FilterLabel
                      sx={{
                        backgroundColor: option.backgroundColor ?? 'inherit',
                      }}
                    >
                      <Typography sx={{ padding: '0 3px' }}>
                        {getLabel(option)}
                      </Typography>
                    </FilterLabel>
                  }
                  control={
                    <Checkbox
                      checked={sprintValues.includes(value)}
                      onChange={e => {
                        if (e.target.checked) {
                          setSprintValues([...sprintValues, value])
                        } else {
                          setSprintValues(sprintValues.filter(v => v !== value))
                        }
                      }}
                      disableRipple={true}
                    />
                  }
                />
              )
            })}
            {/* No Sprint option - at bottom */}
            {!text && (
              <SelectFilterFormControl
                label={intl.formatMessage({
                  id: 'productBacklog.estimatesByAssignee.sprintUnassigned',
                })}
                control={
                  <Checkbox
                    checked={includeNoSprint}
                    onChange={e => setIncludeNoSprint(e.target.checked)}
                    disableRipple={true}
                  />
                }
              />
            )}
          </CheckboxArea>

          {/* Divider */}
          {filteredSprintOptions.length > 0 &&
            filteredPbiOptions.length > 0 && <Divider sx={{ my: 1 }} />}

          {/* PBI Status Section */}
          {filteredPbiOptions.length > 0 && (
            <>
              <SectionLabel>PBI Status</SectionLabel>
              <SelectFilterFormControl
                label="(Select All)"
                control={
                  <Checkbox
                    checked={isPbiAllSelected}
                    indeterminate={!isPbiAllSelected && pbiValues.length > 0}
                    onChange={e =>
                      e.target.checked ? selectAllPbi() : deselectAllPbi()
                    }
                    disableRipple={true}
                  />
                }
              />
              <CheckboxArea>
                {filteredPbiOptions.map(option => {
                  const value = option.status
                  return (
                    <SelectFilterFormControl
                      key={`pbi-filter-${value}`}
                      label={
                        <FilterLabel
                          sx={{
                            backgroundColor:
                              option.backgroundColor ?? 'inherit',
                          }}
                        >
                          <Typography sx={{ padding: '0 3px' }}>
                            {getLabel(option)}
                          </Typography>
                        </FilterLabel>
                      }
                      control={
                        <Checkbox
                          checked={pbiValues.includes(value)}
                          onChange={e => {
                            if (e.target.checked) {
                              setPbiValues([...pbiValues, value])
                            } else {
                              setPbiValues(pbiValues.filter(v => v !== value))
                            }
                          }}
                          disableRipple={true}
                        />
                      }
                    />
                  )
                })}
              </CheckboxArea>
            </>
          )}
        </SelectFilterArea>
        <FilterFooter loading={false} onClick={reset} />
      </BulkSheetFilter>
    )
  }
)

ProductBacklogStatusFilter.displayName = 'ProductBacklogStatusFilter'
